import { Client, IMessage, StompConfig } from '@stomp/stompjs'

export interface WebSocketMessage {
    body: string
    headers: { [key: string]: string }
    command: string
    destination: string
}

export interface WebSocketSubscription {
    id: string
    destination: string
    callback: (message: WebSocketMessage) => void
    stompSubscription?: { unsubscribe: () => void }
}

export class WebSocketConnectionTimeoutError extends Error {
    constructor(timeoutMs: number) {
        super(`WebSocket connection timeout after ${timeoutMs}ms`)
        this.name = 'WebSocketConnectionTimeoutError'
    }
}

class WebSocketService {
    private client: Client | null = null
    private subscriptions: Map<string, WebSocketSubscription> = new Map()
    private isConnected = false
    private reconnectAttempts = 0
    private maxReconnectAttempts = 5
    private reconnectDelay = 5000

    constructor() {
        this.initializeClient()
    }

    private initializeClient() {
        const url = new URL(document.baseURI)
        const proto = url.protocol === 'https:' ? 'wss:' : 'ws:'
        const wsUrl = `${proto}//${url.host}${url.pathname}web/ws`

        const stompConfig: StompConfig = {
            brokerURL: wsUrl,
            reconnectDelay: this.reconnectDelay,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            onConnect: this.onConnect.bind(this),
            onDisconnect: this.onDisconnect.bind(this),
            onStompError: this.onError.bind(this),
        }

        this.client = new Client(stompConfig)
    }

    private onConnect() {
        this.isConnected = true
        this.reconnectAttempts = 0

        // Re-subscribe to all previous subscriptions
        this.subscriptions.forEach((subscription) => {
            if (this.client) {
                const stompSubscription = this.client.subscribe(subscription.destination, (message: IMessage) => {
                    const wsMessage: WebSocketMessage = {
                        body: message.body,
                        headers: message.headers,
                        command: message.command,
                        destination: subscription.destination
                    }
                    subscription.callback(wsMessage)
                }, { id: subscription.id })
                subscription.stompSubscription = stompSubscription
            }
        })
    }

    private onDisconnect() {
        this.isConnected = false
    }

    private onError(_error: any) {
        this.isConnected = false

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++
            setTimeout(() => {
                this.connect().catch(() => {
                    // Reconnect failures are expected during transient outages; the next
                    // onStompError will schedule another attempt until the cap is reached.
                })
            }, this.reconnectDelay * this.reconnectAttempts)
        }
    }

    public connect(timeoutMs = 8000): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.client) {
                reject(new Error('WebSocket client not initialized'))
                return
            }

            if (this.isConnected) {
                resolve()
                return
            }

            this.client.activate()

            const deadline = Date.now() + timeoutMs
            let settled = false

            // Wait for connection with timeout protection
            const checkConnection = () => {
                if (settled) {
                    return
                }
                if (this.isConnected) {
                    settled = true
                    resolve()
                } else if (Date.now() >= deadline) {
                    settled = true
                    // Timeout should terminate this connect attempt and prevent late connect state.
                    this.client?.deactivate()
                    reject(new WebSocketConnectionTimeoutError(timeoutMs))
                } else {
                    setTimeout(checkConnection, 100)
                }
            }
            checkConnection()
        })
    }

    public disconnect() {
        if (this.client && this.isConnected) {
            this.client.deactivate()
        }
    }

    public subscribe(
        destination: string,
        callback: (message: WebSocketMessage) => void,
        subscriptionId?: string
    ): string {
        const id = subscriptionId || `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

        const subscription: WebSocketSubscription = {
            id,
            destination,
            callback
        }

        this.subscriptions.set(id, subscription)

        if (this.client && this.isConnected) {
            const stompSubscription = this.client.subscribe(destination, (message: IMessage) => {
                const wsMessage: WebSocketMessage = {
                    body: message.body,
                    headers: message.headers,
                    command: message.command,
                    destination: destination // Use the subscription destination instead of message.destination
                }
                callback(wsMessage)
            }, { id })
            // Store the STOMP subscription for proper unsubscribe
            subscription.stompSubscription = stompSubscription
        }

        return id
    }

    public unsubscribe(subscriptionId: string) {
        const subscription = this.subscriptions.get(subscriptionId)
        if (subscription) {
            // Use the stored STOMP subscription's unsubscribe method
            if (subscription.stompSubscription) {
                subscription.stompSubscription.unsubscribe()
            }
            this.subscriptions.delete(subscriptionId)
        }
    }

    public send(destination: string, body: string, headers?: { [key: string]: string }) {
        if (!this.client || !this.isConnected) {
            return
        }

        this.client.publish({
            destination,
            body,
            ...(headers !== undefined && { headers })
        })
    }

    public getConnectionStatus(): boolean {
        return this.isConnected
    }

    public getSubscriptions(): WebSocketSubscription[] {
        return Array.from(this.subscriptions.values())
    }
}

// Create singleton instance
export const webSocketService = new WebSocketService()
export default webSocketService
